import React, { useEffect, useRef, useState, useCallback } from 'react';
import './App.css';

const availableShapeTypes = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 15, 20];

const shapeNames = {
  3: 'Triângulo',
  4: 'Quadrilátero', 
  5: 'Pentágono',
  6: 'Hexágono',
  7: 'Heptágono',
  8: 'Octógono',
  9: 'Eneágono',
  10: 'Decágono',
  11: 'Undecágono',
  12: 'Dodecágono',
  15: 'Pentadecágono',
  20: 'Icoságono'
};

function App() {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [shapes, setShapes] = useState([]);
  const animationIdRef = useRef();
  const shapesRef = useRef([]);

  // Carregar high score do localStorage ao iniciar
  useEffect(() => {
    const savedHighScore = localStorage.getItem('trigonometry-game-highscore');
    if (savedHighScore) {
      setHighScore(parseInt(savedHighScore, 10));
    }
  }, []);

  // Atualizar high score quando pontuação mudar
  useEffect(() => {
    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem('trigonometry-game-highscore', score.toString());
    }
  }, [score, highScore]);

  const resetHighScore = () => {
    if (window.confirm('Tem certeza que deseja resetar o recorde?')) {
      setHighScore(0);
      localStorage.removeItem('trigonometry-game-highscore');
    }
  };

  const getRandomColor = () => {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const createShape = useCallback(() => {
    const shapesInUse = shapesRef.current.map(s => s.sides);
    const availableShapes = availableShapeTypes.filter(type => !shapesInUse.includes(type));
    
    if (availableShapes.length === 0) return null;
    
    const sides = availableShapes[Math.floor(Math.random() * availableShapes.length)];
    return {
      id: Date.now() + Math.random(),
      sides,
      x: Math.random() * 600 + 100,
      y: -50,
      speed: Math.random() * 2 + 1,
      color: getRandomColor()
    };
  }, []);

  // Manter shapesRef sincronizado
  useEffect(() => {
    shapesRef.current = shapes;
  }, [shapes]);

  const drawShape = (ctx, shape) => {
    ctx.fillStyle = shape.color;
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    
    const radius = 30;
    const centerX = shape.x;
    const centerY = shape.y;
    
    for (let i = 0; i < shape.sides; i++) {
      const angle = (i * 2 * Math.PI) / shape.sides - Math.PI / 2;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Adicionar legenda com o número de lados
    ctx.fillStyle = '#000';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Fundo branco para a legenda
    const text = shape.sides.toString();
    const textMetrics = ctx.measureText(text);
    const textWidth = textMetrics.width;
    const textHeight = 20;
    
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillRect(centerX - textWidth/2 - 4, centerY - textHeight/2 - 2, textWidth + 8, textHeight + 4);
    
    // Borda da legenda
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.strokeRect(centerX - textWidth/2 - 4, centerY - textHeight/2 - 2, textWidth + 8, textHeight + 4);
    
    // Texto da legenda
    ctx.fillStyle = '#000';
    ctx.fillText(text, centerX, centerY);
  };

  const explodeShape = (shape) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let explosionRadius = 0;
    const maxRadius = 100;
    
    const animate = () => {
      // Limpar apenas a área da explosão
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(shape.x, shape.y, explosionRadius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();
      
      // Desenhar efeito de explosão
      ctx.strokeStyle = shape.color;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(shape.x, shape.y, explosionRadius, 0, 2 * Math.PI);
      ctx.stroke();
      
      explosionRadius += 5;
      if (explosionRadius < maxRadius) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
  };

  const handleButtonClick = (sides) => {
    const shape = shapesRef.current.find(s => s.sides === sides);
    if (shape) {
      // Acertou
      const points = sides === 4 ? 4 : sides;
      setScore(prevScore => prevScore + points);
      
      // Remover a forma e fazer explosão
      setShapes(prevShapes => prevShapes.filter(s => s.id !== shape.id));
      explodeShape(shape);
    } else {
      // Errou - reduzir 1 ponto
      setScore(prevScore => prevScore - 1);
    }
  };

  // Game loop separado
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const gameLoop = () => {
      // Limpar canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Atualizar posições das formas
      const currentShapes = shapesRef.current;
      const updatedShapes = currentShapes.map(shape => ({
        ...shape,
        y: shape.y + shape.speed
      })).filter(shape => {
        // Se a forma saiu da tela, aplicar penalidade
        if (shape.y > canvas.height + 50) {
          const penalty = shape.sides === 4 ? 8 : 2 * shape.sides;
          setScore(prevScore => prevScore - penalty);
          return false;
        }
        return true;
      });
      
      // Atualizar estado apenas se houver mudanças
      if (updatedShapes.length !== currentShapes.length || 
          updatedShapes.some((shape, index) => shape.y !== currentShapes[index]?.y)) {
        setShapes(updatedShapes);
      }
      
      // Desenhar todas as formas
      updatedShapes.forEach(shape => {
        drawShape(ctx, shape);
      });
      
      animationIdRef.current = requestAnimationFrame(gameLoop);
    };
    
    gameLoop();
    
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, []); // Array vazio - roda apenas uma vez

  // Spawnar novas formas em intervalo fixo
  useEffect(() => {
    const spawnInterval = setInterval(() => {
      const newShape = createShape();
      if (newShape) {
        setShapes(prevShapes => [...prevShapes, newShape]);
      }
    }, 3000);
    
    return () => clearInterval(spawnInterval);
  }, [createShape]);

  // Spawnar primeira forma
  useEffect(() => {
    const firstShape = createShape();
    if (firstShape) {
      setShapes([firstShape]);
    }
  }, [createShape]);

  return (
    <div className="App">
      <h1>Jogo de Trigonometria</h1>
      <div className="game-container">
        <canvas 
          ref={canvasRef} 
          width={800} 
          height={600} 
          style={{ border: '2px solid #333', backgroundColor: '#f0f0f0' }}
        />
        <div className="controls">
          <div className="scoreboard">
            <h2>Pontuação: {score}</h2>
            <h3 className="high-score">🏆 Recorde: {highScore}</h3>
            {score === highScore && score > 0 && (
              <div className="new-record">🎉 NOVO RECORDE!</div>
            )}
            {highScore > 0 && (
              <button className="reset-button" onClick={resetHighScore}>
                🔄 Resetar Recorde
              </button>
            )}
          </div>
          <div className="buttons">
            {availableShapeTypes.map(sides => (
              <button 
                key={sides} 
                onClick={() => handleButtonClick(sides)}
                className="shape-button"
                title={`${sides} lados`}
              >
                {shapeNames[sides]}
              </button>
            ))}
          </div>
          <div className="rules">
            <h3>📋 Regras do Jogo</h3>
            <div className="rules-content">
              <div className="rule-section">
                <h4>🎯 Objetivo</h4>
                <p>Identifique o número de lados das figuras que caem e clique no botão correto.</p>
              </div>
              
              <div className="rule-section">
                <h4>✅ Pontuação</h4>
                <ul>
                  <li><strong>Acerto:</strong> +número de lados</li>
                  <li><strong>Quadrilátero:</strong> +4 pontos (especial)</li>
                </ul>
              </div>
              
              <div className="rule-section">
                <h4>❌ Penalidades</h4>
                <ul>
                  <li><strong>Clique errado:</strong> -1 ponto</li>
                  <li><strong>Figura que cai:</strong> -2×lados</li>
                  <li><strong>Quadrilátero que cai:</strong> -8 pontos</li>
                </ul>
              </div>
              
              <div className="rule-section">
                <h4>💡 Dicas</h4>
                <ul>
                  <li>Observe a legenda no centro da figura</li>
                  <li>Figuras com mais lados valem mais pontos</li>
                  <li>Pense antes de clicar para evitar penalidades</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <footer className="footer">
        <div className="footer-content">
          <p>
            Idealizado por <strong>Diana Calheiro</strong> • 
            Desenvolvido para <a href="https://sarapiqua.com.br" target="_blank" rel="noopener noreferrer">sarapiqua.com.br</a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
